import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import type {
  AffiliatePackagesQuery,
  AffiliateScope
} from '@/Network/Types/Requests/AntecipeAffiliateRequests'
import type {
  AffiliateMeResponse,
  AffiliatePackageDetailResponse,
  AffiliatePackageRow,
  AffiliatePackagesResponse,
  AffiliatePayoutResponse,
  AffiliateSummaryResponse,
  AntecipeAffiliateListItem
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'

const ATTENTION_PAGE_SIZE = 5

const scopeKey = (scope: AffiliateScope): string =>
  scope.kind === 'self' ? 'self' : `admin:${scope.affiliateId}`

/**
 * Estado do Portal do Afiliado. Um store só para o afiliado e para o "ver como":
 * o `scope` diz de quem são os dados. Cada carga guarda a geração em que começou e
 * descarta a resposta se o escopo (ou a própria carga) mudou no meio do caminho:
 * a última requisição vence e `setScope` invalida tudo o que estava em voo.
 */
export const useAntecipeAffiliateStore = defineStore('antecipeAffiliate', () => {
  const scope = ref<AffiliateScope>({ kind: 'self' })

  const me = ref<AffiliateMeResponse | null>(null)
  const meError = ref<string | null>(null)
  const isLoadingMe = ref(false)

  const summary = ref<AffiliateSummaryResponse | null>(null)
  const summaryError = ref<string | null>(null)
  const isLoadingSummary = ref(false)
  const attention = ref<AffiliatePackageRow[]>([])

  const packages = ref<AffiliatePackagesResponse | null>(null)
  const packagesError = ref<string | null>(null)
  const isLoadingPackages = ref(false)

  const payout = ref<AffiliatePayoutResponse | null>(null)
  const payoutError = ref<string | null>(null)
  const isLoadingPayout = ref(false)

  const unitOpenCounts = ref<Record<number, number>>({})

  const drawerPackageId = ref<number | null>(null)
  const drawer = ref<AffiliatePackageDetailResponse | null>(null)
  const drawerError = ref<string | null>(null)
  const isLoadingDrawer = ref(false)

  const affiliates = ref<AntecipeAffiliateListItem[]>([])
  const affiliatesError = ref<string | null>(null)
  const affiliatesForbidden = ref(false)
  const isLoadingAffiliates = ref(false)

  // Uma geração por carga: nova chamada da mesma carga ou troca de escopo
  // incrementa o contador, e a resposta antiga é descartada ao chegar.
  let meGeneration = 0
  let summaryGeneration = 0
  let packagesGeneration = 0
  let payoutGeneration = 0
  let unitCountsGeneration = 0
  let drawerGeneration = 0
  let affiliatesGeneration = 0

  const currentPolicy = computed(
    () => me.value?.payoutPolicies.find((policy) => policy.current) ?? null
  )
  // Sem fallback para o total filtrado da lista (Ruling F13): o selo fica vazio
  // até o resumo carregar, para nunca mostrar uma contagem filtrada como total.
  const openPackageCount = computed<number | null>(() => summary.value?.openPackageCount ?? null)

  function closePackage(): void {
    drawerGeneration += 1
    drawerPackageId.value = null
    drawer.value = null
    drawerError.value = null
    isLoadingDrawer.value = false
  }

  function setScope(next: AffiliateScope): void {
    if (scopeKey(next) === scopeKey(scope.value)) return
    meGeneration += 1
    summaryGeneration += 1
    packagesGeneration += 1
    payoutGeneration += 1
    unitCountsGeneration += 1
    scope.value = next
    me.value = null
    meError.value = null
    isLoadingMe.value = false
    summary.value = null
    summaryError.value = null
    isLoadingSummary.value = false
    attention.value = []
    packages.value = null
    packagesError.value = null
    isLoadingPackages.value = false
    payout.value = null
    payoutError.value = null
    isLoadingPayout.value = false
    unitOpenCounts.value = {}
    closePackage()
  }

  async function loadMe(force = false): Promise<void> {
    if (me.value && !force) return
    meGeneration += 1
    const generation = meGeneration
    isLoadingMe.value = true
    meError.value = null
    try {
      const data = await Network.antecipeAffiliate.getMe(scope.value)
      if (generation === meGeneration) me.value = data
    } catch (e) {
      if (generation !== meGeneration) return
      meError.value =
        getApiErrorStatus(e) === 403
          ? 'Sem acesso a este afiliado.'
          : getApiErrorMessage(e, 'Não foi possível carregar os dados do afiliado.')
    } finally {
      if (generation === meGeneration) isLoadingMe.value = false
    }
  }

  async function loadSummary(month: string): Promise<void> {
    summaryGeneration += 1
    const generation = summaryGeneration
    isLoadingSummary.value = true
    summaryError.value = null
    try {
      const [data, open] = await Promise.all([
        Network.antecipeAffiliate.getSummary(scope.value, month),
        Network.antecipeAffiliate.listPackages(scope.value, {
          tab: 'open',
          page: 1,
          pageSize: ATTENTION_PAGE_SIZE
        })
      ])
      if (generation !== summaryGeneration) return
      summary.value = data
      attention.value = open.items
    } catch (e) {
      if (generation === summaryGeneration) {
        summaryError.value = getApiErrorMessage(e, 'Não foi possível carregar a visão geral.')
      }
    } finally {
      if (generation === summaryGeneration) isLoadingSummary.value = false
    }
  }

  async function loadPackages(query: AffiliatePackagesQuery): Promise<void> {
    packagesGeneration += 1
    const generation = packagesGeneration
    isLoadingPackages.value = true
    packagesError.value = null
    try {
      const data = await Network.antecipeAffiliate.listPackages(scope.value, query)
      if (generation === packagesGeneration) packages.value = data
    } catch (e) {
      if (generation === packagesGeneration) {
        packagesError.value = getApiErrorMessage(e, 'Não foi possível carregar os pacotes.')
      }
    } finally {
      if (generation === packagesGeneration) isLoadingPackages.value = false
    }
  }

  async function loadUnitOpenCounts(): Promise<void> {
    if (!me.value) return
    unitCountsGeneration += 1
    const generation = unitCountsGeneration
    const units = me.value.units
    const results = await Promise.allSettled(
      units.map((unit) =>
        Network.antecipeAffiliate.listPackages(scope.value, {
          tab: 'open',
          unitId: unit.healthUnitId,
          page: 1,
          pageSize: 1
        })
      )
    )
    if (generation !== unitCountsGeneration) return
    const counts: Record<number, number> = {}
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') counts[units[index].healthUnitId] = result.value.total
    })
    unitOpenCounts.value = counts
  }

  async function openPackage(id: number): Promise<void> {
    drawerGeneration += 1
    const generation = drawerGeneration
    drawerPackageId.value = id
    drawer.value = null
    drawerError.value = null
    isLoadingDrawer.value = true
    try {
      const data = await Network.antecipeAffiliate.getPackage(scope.value, id)
      if (generation === drawerGeneration) drawer.value = data
    } catch (e) {
      if (generation !== drawerGeneration) return
      drawerError.value =
        getApiErrorStatus(e) === 404
          ? 'Pacote não encontrado nas unidades deste afiliado.'
          : getApiErrorMessage(e, 'Não foi possível carregar o pacote.')
    } finally {
      if (generation === drawerGeneration) isLoadingDrawer.value = false
    }
  }

  async function loadPayout(month: string): Promise<void> {
    payoutGeneration += 1
    const generation = payoutGeneration
    isLoadingPayout.value = true
    payoutError.value = null
    try {
      const data = await Network.antecipeAffiliate.getPayout(scope.value, month)
      if (generation === payoutGeneration) payout.value = data
    } catch (e) {
      if (generation === payoutGeneration) {
        payoutError.value = getApiErrorMessage(e, 'Não foi possível carregar o repasse.')
      }
    } finally {
      if (generation === payoutGeneration) isLoadingPayout.value = false
    }
  }

  async function loadAffiliates(): Promise<void> {
    affiliatesGeneration += 1
    const generation = affiliatesGeneration
    isLoadingAffiliates.value = true
    affiliatesError.value = null
    affiliatesForbidden.value = false
    try {
      const data = await Network.antecipeAffiliate.listAffiliates()
      if (generation === affiliatesGeneration) affiliates.value = data
    } catch (e) {
      if (generation !== affiliatesGeneration) return
      if (getApiErrorStatus(e) === 403) affiliatesForbidden.value = true
      else affiliatesError.value = getApiErrorMessage(e, 'Não foi possível carregar os afiliados.')
    } finally {
      if (generation === affiliatesGeneration) isLoadingAffiliates.value = false
    }
  }

  return {
    scope,
    me,
    meError,
    isLoadingMe,
    summary,
    summaryError,
    isLoadingSummary,
    attention,
    packages,
    packagesError,
    isLoadingPackages,
    payout,
    payoutError,
    isLoadingPayout,
    unitOpenCounts,
    drawerPackageId,
    drawer,
    drawerError,
    isLoadingDrawer,
    affiliates,
    affiliatesError,
    affiliatesForbidden,
    isLoadingAffiliates,
    currentPolicy,
    openPackageCount,
    setScope,
    loadMe,
    loadSummary,
    loadPackages,
    loadUnitOpenCounts,
    openPackage,
    closePackage,
    loadPayout,
    loadAffiliates
  }
})
